import React, { useCallback, useRef, ReactNode } from "react";
import {
	PublicClientApplication,
} from "@azure/msal-browser";
import { BaseAuthProvider, LoginProvider, useAuth } from "@cuntas-mitool/ui";

export { useAuth };

const CLIENT_ID = process.env.CUNTAS_MITOOL_AZURE_CLIENT_ID!;
const GOOGLE_CLIENT_ID = process.env.GOOGLE_CLIENT_ID!;

/**
 * Acquire a Microsoft access token using standard MSAL popup.
 * Attempts silent token acquisition first; falls back to loginPopup.
 */
async function acquireMicrosoftTokenWeb(
	msalRef: React.MutableRefObject<PublicClientApplication | null>
): Promise<string> {
	if (!msalRef.current) {
		const msalConfig = {
			auth: {
				clientId: CLIENT_ID,
				authority: "https://login.microsoftonline.com/common",
				redirectUri: `${window.location.origin}/authredirect.html`,
			},
			cache: { cacheLocation: "localStorage" as const },
		};
		msalRef.current = new PublicClientApplication(msalConfig);
		await msalRef.current.initialize();
		console.log("[WebAuth] Standard MSAL initialized");
	}

	const msalInstance = msalRef.current;
	const accounts = msalInstance.getAllAccounts();

	if (accounts.length > 0) {
		try {
			const silentResult = await msalInstance.acquireTokenSilent({
				scopes: ["User.Read"],
				account: accounts[0],
			});
			console.log("[WebAuth] Got Microsoft token silently");
			return silentResult.accessToken;
		} catch {
			console.log("[WebAuth] Silent token failed, falling back to popup");
		}
	}

	const result = await msalInstance.loginPopup({
		scopes: ["User.Read"],
		prompt: "select_account",
	});
	console.log("[WebAuth] Microsoft popup login successful");
	return result.accessToken;
}

/**
 * Acquire a Google ID token using a popup window + postMessage.
 * Opens a small popup that redirects to Google OAuth, then sends the token back.
 */
function acquireGoogleTokenWeb(): Promise<string> {
	return new Promise((resolve, reject) => {
		const redirectUri = `${window.location.origin}/webapp/googlecallback.html`;
		const nonce = Math.random().toString(36).substring(2);
		const scope = encodeURIComponent("openid email profile");

		const url =
			`https://accounts.google.com/o/oauth2/v2/auth` +
			`?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}` +
			`&redirect_uri=${encodeURIComponent(redirectUri)}` +
			`&response_type=id_token` +
			`&scope=${scope}` +
			`&nonce=${nonce}`;

		const popup = window.open(url, "google-auth", "width=500,height=620,resizable=yes");

		if (!popup) {
			reject(new Error("Popup was blocked. Please allow popups for this site."));
			return;
		}

		const handler = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;
			if (!event.data || event.data.type !== "GOOGLE_AUTH_TOKEN") return;

			window.removeEventListener("message", handler);
			clearInterval(closedCheck);

			if (event.data.token) {
				console.log("[WebAuth] Received Google token via postMessage");
				resolve(event.data.token as string);
			} else {
				reject(new Error(event.data.error || "Google authentication failed"));
			}
		};

		window.addEventListener("message", handler);

		// Detect if user closes the popup without completing auth
		const closedCheck = setInterval(() => {
			if (popup.closed) {
				clearInterval(closedCheck);
				window.removeEventListener("message", handler);
				reject(new Error("Authentication cancelled"));
			}
		}, 500);
	});
}

interface WebAuthProviderProps {
	children: ReactNode;
}

export function WebAuthProvider({ children }: WebAuthProviderProps) {
	const msalRef = useRef<PublicClientApplication | null>(null);

	const acquireProviderToken = useCallback(async (provider: LoginProvider): Promise<string> => {
		if (provider === "google") {
			return acquireGoogleTokenWeb();
		}
		return acquireMicrosoftTokenWeb(msalRef);
	}, []);

	return (
		<BaseAuthProvider acquireProviderToken={acquireProviderToken}>
			{children}
		</BaseAuthProvider>
	);
}
